import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrapper } from "components/core/Header/styles.header";
import BackButton from "../Button/BackButton";
import { Loader } from "../Common/Loader";
import ShowToast from "../Common/Toast";
import { api } from "network/network";
import { API_HOST_WMS } from "config/api.config";
import { APIendpoints } from "utils/constants/APIendpoints";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import { STATUS_BEGIN } from "utils/constants/others";
import usePayoutsStore from "./usePayoutsStore";
import TransactionDetailSheet from "./TransactionDetailSheet";
import { copyToClipboard, formatMoney } from "./payoutsFormat.js";
import {
  Screen,
  TabBar,
  TabItem,
  FilterRow,
  FilterChip,
  ListHeader,
  CountText,
  AdviceLink,
  Row,
  RowIcon,
  RowBody,
  RowTitle,
  RowSubtitle,
  RowRight,
  Amount,
  StatusPill,
  EmptyState,
  ScrollList,
  DateChipWrapper,
  SheetOverlay,
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetCloseButton,
  OptionsCard,
  OptionRow,
  OptionHeader,
  OptionLabel,
  RadioOuter,
  RadioInner,
  CustomDateRow,
  DateField,
  DateFieldTitle,
  DateFieldInput,
  ApplyButton,
  CopyButton,
  HeaderGroup,
  HeaderBackWrap,
  HeaderText,
  HeaderTitle,
  HeaderSubtitle,
  HeaderIconButton,
  SearchBar,
  SearchBackButton,
  SearchField,
  SearchInput,
} from "./styles.payouts.js";

export const PAYOUTS_TABS = {
  PAYMENTS: "PAYMENTS",
  TRANSACTIONS: "TRANSACTIONS",
};

const PAYMENT_STATUS_FILTERS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "paid", label: "Paid" },
];

const TRANSACTION_STATUS_FILTERS = [
  { key: "invoices", label: "Invoices" },
  { key: "return_notes", label: "Return notes" },
  { key: "debit_note", label: "Debit note" },
  { key: "other", label: "Other" },
];

const PAYMENT_STATUS_CODES = {
  upcoming: ["PENDING", "QUEUED_FOR_PAYOUT", "FAILED"],
  paid: ["PAID"],
};

const TRANSACTION_TYPE_CODES = {
  invoices: "GRN_COMPLETE",
  return_notes: "PRN",
  debit_note: "DN",
};

const OTHER_EXCLUDABLE_TYPES = ["GRN_COMPLETE", "PRN", "DN"];

const buildPaymentStatusParam = (selectedKeys) => {
  if (!selectedKeys.length) return undefined;
  const codes = selectedKeys.flatMap((k) => PAYMENT_STATUS_CODES[k] || []);
  return codes.length ? codes.join(",") : undefined;
};

const buildTransactionParams = (selectedKeys) => {
  if (!selectedKeys.length) return {};
  const hasOther = selectedKeys.includes("other");
  const specificCodes = selectedKeys
    .filter((k) => k !== "other")
    .map((k) => TRANSACTION_TYPE_CODES[k])
    .filter(Boolean);

  if (hasOther) {
    const excluded = OTHER_EXCLUDABLE_TYPES.filter(
      (code) => !specificCodes.includes(code)
    );
    return excluded.length
      ? { excluded_transaction_types: excluded.join(",") }
      : {};
  }
  return specificCodes.length
    ? { transaction_types: specificCodes.join(",") }
    : {};
};

const DEFAULT_PAGE_SIZE = 50;

const FROM_ENTITY = { id: "HYPERPURE", type: "BUSINESS" };
const TO_ENTITY_TYPE = "VENDOR";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const DOWNLOAD_ERROR_MESSAGE =
  "Unable to download payment advice. Please try again.";

const FILE_POLL_INTERVAL_MS = 1000;
const FILE_POLL_MAX_RETRIES = 10;

// The presigned url is issued before the file is uploaded in the background,
// so keep requesting it until the object exists (200), retries run out or the
// signal is aborted (user left the page).
const waitForFile = async (url, signal) => {
  for (let attempt = 0; attempt <= FILE_POLL_MAX_RETRIES; attempt += 1) {
    if (signal.aborted) return false;
    try {
      const result = await fetch(url, { signal });
      if (result.status === 200) {
        result.body?.cancel();
        return true;
      }
    } catch (error) {
      // not available yet (or aborted), retry
    }
    if (attempt < FILE_POLL_MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, FILE_POLL_INTERVAL_MS));
    }
  }
  return false;
};

const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="24 46 20 20" fill="none" aria-hidden="true">
    <path
      d="M37.0399 50.8799C37.3199 51.1799 37.3199 51.6599 37.0399 51.9399L32.5599 56.4199L37.0399 60.8799C37.3199 61.1799 37.3199 61.6599 37.0399 61.9399C36.7599 62.2199 36.2599 62.2399 35.9799 61.9399L30.9799 56.9399C30.6799 56.6599 30.6799 56.1799 30.9799 55.8799L35.9799 50.8799C36.2599 50.5999 36.7399 50.5999 37.0399 50.8799Z"
      fill="#293142"
    />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="318 48 16 16" fill="none" aria-hidden="true">
    <path
      d="M325.016 49.5738C322.011 49.5738 319.574 52.0105 319.574 55.0164C319.574 58.0223 322.011 60.459 325.016 60.459C328.022 60.459 330.459 58.0223 330.459 55.0164C330.459 52.0105 328.022 49.5738 325.016 49.5738ZM318 55.0164C318 51.1413 321.141 48 325.016 48C328.891 48 332.033 51.1413 332.033 55.0164C332.033 56.6704 331.46 58.1908 330.503 59.3902L333.77 62.6567C334.077 62.964 334.077 63.4622 333.77 63.7695C333.462 64.0768 332.964 64.0768 332.657 63.7695L329.39 60.503C328.191 61.4605 326.67 62.0328 325.016 62.0328C321.141 62.0328 318 58.8914 318 55.0164Z"
      fillRule="evenodd"
      clipRule="evenodd"
      fill="#293142"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M8.748 12.996C8.658 12.96 8.586 12.906 8.532 12.852L4.032 8.352C3.762 8.082 3.762 7.65 4.032 7.398C4.302 7.146 4.716 7.128 4.986 7.398L8.334 10.746V0.666C8.334 0.306 8.622 0 9 0C9.378 0 9.684 0.306 9.684 0.666V10.746L13.032 7.398C13.284 7.128 13.716 7.128 13.986 7.398C14.256 7.668 14.238 8.082 13.986 8.352L9.486 12.852C9.414 12.906 9.342 12.96 9.252 12.996C9.18 13.032 9.09 13.05 9 13.05C8.91 13.05 8.82 13.032 8.748 12.996ZM16.65 11.466C16.65 11.106 16.956 10.8 17.334 10.8C17.712 10.8 18 11.106 18 11.466V15.516C18 16.884 16.866 18 15.48 18H2.52C1.134 18 0 16.884 0 15.516V11.466C0 11.106 0.306 10.8 0.684 10.8C1.062 10.8 1.35 11.106 1.35 11.466V15.516C1.35 16.146 1.872 16.65 2.52 16.65H15.48C16.128 16.65 16.65 16.146 16.65 15.516V11.466Z"
      fill="currentColor"
    />
  </svg>
);

// The backend reads these as Indian time, so send the day boundaries with the IST offset.
const IST_OFFSET = "+05:30";
const toRFC3339Start = (yyyyMmDd) =>
  yyyyMmDd ? `${yyyyMmDd}T00:00:00${IST_OFFSET}` : undefined;
const toRFC3339End = (yyyyMmDd) =>
  yyyyMmDd ? `${yyyyMmDd}T23:59:59${IST_OFFSET}` : undefined;

const toYyyyMmDd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultDueRange = () => {
  const now = new Date();
  const from = new Date(now);
  from.setDate(now.getDate() - 15);
  const to = new Date(now);
  to.setDate(now.getDate() + 5);
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) };
};

const formatChipDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [y, m, d] = yyyyMmDd.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};

const DURATION_PRESETS = [
  { key: "last_7", label: "Last 7 days", days: 7 },
  { key: "last_30", label: "Last 30 days", days: 30 },
  { key: "last_6m", label: "Last 6 months", months: 6 },
  { key: "custom", label: "Chose date" },
];

const computePresetRange = (preset) => {
  const to = new Date();
  const from = new Date();
  if (preset.days) {
    from.setDate(to.getDate() - preset.days);
  } else if (preset.months) {
    from.setMonth(to.getMonth() - preset.months);
  }
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) };
};

const DateRangeChip = ({ from, to, onApply }) => {
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("custom");
  const [draftFrom, setDraftFrom] = useState(from || "");
  const [draftTo, setDraftTo] = useState(to || "");

  const label =
    from && to
      ? `Due: ${formatChipDate(from)} to ${formatChipDate(to)}`
      : "Due date";

  const openSheet = () => {
    setDraftFrom(from || "");
    setDraftTo(to || "");
    setSelectedPreset("custom");
    setOpen(true);
  };

  const closeSheet = () => setOpen(false);

  const handleSelectPreset = (preset) => {
    setSelectedPreset(preset.key);
    if (preset.key !== "custom") {
      const range = computePresetRange(preset);
      setDraftFrom(range.from);
      setDraftTo(range.to);
    } else {
      setDraftFrom((prev) => prev || from || "");
      setDraftTo((prev) => prev || to || "");
    }
  };

  const canApply = draftFrom && draftTo && draftFrom <= draftTo;

  const handleApply = () => {
    if (!canApply) return;
    onApply({ from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <DateChipWrapper>
      <FilterChip active={Boolean(from && to)} onClick={openSheet}>
        {label} ▾
      </FilterChip>
      {open ? (
        <SheetOverlay onClick={closeSheet}>
          <Sheet onClick={stopPropagation}>
            <SheetHeader>
              <SheetTitle>Select duration</SheetTitle>
              <SheetCloseButton onClick={closeSheet} aria-label="Close">
                ✕
              </SheetCloseButton>
            </SheetHeader>
            <OptionsCard>
              {DURATION_PRESETS.map((preset) => {
                const active = selectedPreset === preset.key;
                return (
                  <OptionRow
                    key={preset.key}
                    onClick={() => handleSelectPreset(preset)}
                  >
                    <OptionHeader>
                      <OptionLabel>{preset.label}</OptionLabel>
                      <RadioOuter active={active}>
                        <RadioInner active={active} />
                      </RadioOuter>
                    </OptionHeader>
                    {preset.key === "custom" && active ? (
                      <CustomDateRow onClick={stopPropagation}>
                        <DateField>
                          <DateFieldTitle>From</DateFieldTitle>
                          <DateFieldInput
                            type="date"
                            value={draftFrom}
                            onChange={(e) => setDraftFrom(e.target.value)}
                          />
                        </DateField>
                        <DateField>
                          <DateFieldTitle>To</DateFieldTitle>
                          <DateFieldInput
                            type="date"
                            value={draftTo}
                            onChange={(e) => setDraftTo(e.target.value)}
                          />
                        </DateField>
                      </CustomDateRow>
                    ) : null}
                  </OptionRow>
                );
              })}
            </OptionsCard>
            <ApplyButton disabled={!canApply} onClick={handleApply}>
              Apply
            </ApplyButton>
          </Sheet>
        </SheetOverlay>
      ) : null}
    </DateChipWrapper>
  );
};

// Tag + subtitle rules for a payment row:
//   tag comes from the backend (Paid, Adjusted, Upcoming, Carry forward)
//   payout_utr_number present -> subtitle: "UTR: <utr>" (copyable)
//   tag = "Carry forward" AND payout_amount < 0
//                             -> subtitle: "Will be adjusted in next payout"
//   otherwise                 -> no subtitle (date centers vertically)
const getPaymentTagAndSubtitle = (item) => {
  const tag = item.tag;
  const utr = item.payout_utr_number;
  const isNegative = Number(item.payout_amount || 0) < 0;

  if (utr) {
    const displayUtr = utr.length > 20 ? `${utr.slice(0, 18)}..` : utr;
    return { tag, subtitle: `UTR: ${displayUtr}`, showCopy: true };
  }
  return {
    tag,
    subtitle:
      (tag || "").toLowerCase() === "carry forward" && isNegative
        ? "Will be adjusted in next payout"
        : "",
    showCopy: false,
  };
};

const PaymentRow = ({ item, onClick }) => {
  const isDebit = Number(item.payout_amount) < 0;
  const { tag, subtitle, showCopy } = getPaymentTagAndSubtitle(item);
  return (
    <Row onClick={onClick} clickable={Boolean(onClick)}>
      <RowIcon>
        <img src="/logos/rupee_icon.png" alt="" />
      </RowIcon>
      <RowBody>
        <RowTitle>{formatDate(item.accrual_date)}</RowTitle>
        {subtitle ? (
          <RowSubtitle>
            {subtitle}
            {showCopy ? (
              <CopyButton
                type="button"
                onClick={() => copyToClipboard(item.payout_utr_number)}
                aria-label="Copy UTR"
              >
                ⎘
              </CopyButton>
            ) : null}
          </RowSubtitle>
        ) : null}
      </RowBody>
      <RowRight>
        <Amount negative={isDebit}>{formatMoney(item.payout_amount, { spaced: true })}</Amount>
        {tag ? <StatusPill status={tag}>{tag}</StatusPill> : null}
      </RowRight>
    </Row>
  );
};

// Tag + amount rules for a transaction row:
//   tag comes from the backend (Paid, Adjusted, Upcoming, Carry forward, Hold)
//
//   Amount: transaction_amount exactly as the backend sends it
//
//   Amount suffix:
//     transaction_type = "GRN_COMPLETE" AND transaction_status != "SUCCESS"
//       -> append "(estd.)" in gray (estimated GRN amount, not yet finalized)
const TransactionRow = ({ item, onClick }) => {
  const transactionStatus = (item.transaction_status || "").toUpperCase();
  const transactionType = (item.transaction_type || "").toUpperCase();
  const tag = item.tag;
  const showEstimated =
    transactionType === "GRN_COMPLETE" && transactionStatus !== "SUCCESS";
  const dueLabel = item.consideration_date
    ? `Due ${formatDate(item.consideration_date)}`
    : item.actual_payment_date
    ? `Paid on ${formatDate(item.actual_payment_date)}`
    : "";
  const label =
    item.display_transaction_type ||
    item.parent_transaction_type ||
    item.transaction_type ||
    "Invoice";
  return (
    <Row onClick={onClick} clickable={Boolean(onClick)}>
      <RowIcon>
        <img src="/logos/transaction_document.png" alt="" />
      </RowIcon>
      <RowBody>
        <RowTitle>{label}</RowTitle>
        <RowSubtitle>{item.transaction_number}</RowSubtitle>
        {dueLabel ? <RowSubtitle>{dueLabel}</RowSubtitle> : null}
      </RowBody>
      <RowRight>
        <Amount>
          {formatMoney(item.transaction_amount, { spaced: true })}
          {showEstimated ? (
            <span style={{ color: "#9197a6", marginLeft: 4, fontWeight: 400 }}>
              (est.)
            </span>
          ) : null}
        </Amount>
        {tag ? <StatusPill status={tag}>{tag}</StatusPill> : null}
      </RowRight>
    </Row>
  );
};

const Payouts = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(PAYOUTS_TABS.PAYMENTS);
  const [paymentsFilter, setPaymentsFilter] = useState([]);
  const [transactionsFilter, setTransactionsFilter] = useState([]);
  const [dueRange, setDueRange] = useState(getDefaultDueRange);

  const toEntityId =
    (typeof window !== "undefined" &&
      window.localStorage.getItem(LS_KEYS.OUTLET_ID)) ||
    "";

  const vendorName =
    (typeof window !== "undefined" &&
      window.localStorage.getItem(LS_KEYS.OUTLET_NAME)) ||
    "";

  const handleBackClick = () => navigate("/");

  const handlePaymentClick = (referenceNumber) =>
    navigate(
      `/payouts/details?reference_number=${encodeURIComponent(referenceNumber)}`
    );

  const {
    accruals,
    accrualsStatus,
    accrualsHasMore,
    transactions,
    transactionsStatus,
    transactionsHasMore,
    getVendorAccruals,
    getVendorTransactions,
    resetAccruals,
    resetTransactions,
  } = usePayoutsStore();

  const scrollRef = useRef(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  // searchDraft is what is typed, searchTerm is what was submitted with enter
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const downloadAbortRef = useRef(null);

  // stop polling (and further state updates) if the user leaves mid-download
  useEffect(() => () => downloadAbortRef.current?.abort(), []);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError("");
    const controller = new AbortController();
    downloadAbortRef.current = controller;
    try {
      const response = await api.get(
        API_HOST_WMS,
        APIendpoints.downloadVendorAccrualTransactions,
        {
          query: {
            from_entity_id: FROM_ENTITY.id,
            from_entity_type: FROM_ENTITY.type,
            to_entity_id: toEntityId,
            to_entity_type: TO_ENTITY_TYPE,
            accrual_start_date: toRFC3339Start(dueRange.from),
            accrual_end_date: toRFC3339End(dueRange.to),
            fetch_details: false,
            business_entity: FROM_ENTITY.id,
            is_detailed_view_required: true,
          },
        }
      );
      const presignedUrl = response?.data?.response?.presigned_url;
      const ready =
        presignedUrl && (await waitForFile(presignedUrl, controller.signal));
      if (controller.signal.aborted) return;
      if (!ready) {
        setDownloadError(DOWNLOAD_ERROR_MESSAGE);
      } else {
        // a plain link click downloads the file in place, no new tab and no
        // popup blocker involved even though it runs after the polling wait
        const link = document.createElement("a");
        link.href = presignedUrl;
        link.click();
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Payment advice download failed", error);
      setDownloadError(
        error?.response?.data?.error?.message || DOWNLOAD_ERROR_MESSAGE
      );
    } finally {
      if (!controller.signal.aborted) setDownloading(false);
    }
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchDraft("");
    setSearchTerm("");
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchDraft.trim());
    // dismiss the mobile keyboard once the search is submitted
    document.activeElement?.blur();
  };

  const switchTab = (nextTab) => {
    if (nextTab === activeTab) return;
    if (nextTab === PAYOUTS_TABS.PAYMENTS) {
      resetTransactions();
    } else {
      resetAccruals();
    }
    closeSearch();
    setActiveTab(nextTab);
  };

  const fetchAccruals = useCallback(
    (offset, append) => {
      getVendorAccruals(
        {
          page_start_offset: offset,
          page_size: DEFAULT_PAGE_SIZE,
          fetch_details: false,
          from_entity_id: FROM_ENTITY.id,
          from_entity_type: FROM_ENTITY.type,
          to_entity_id: toEntityId,
          to_entity_type: TO_ENTITY_TYPE,
          accrual_start_date: toRFC3339Start(dueRange.from),
          accrual_end_date: toRFC3339End(dueRange.to),
          accrual_statuses: buildPaymentStatusParam(paymentsFilter),
        },
        { append }
      );
    },
    [toEntityId, dueRange, paymentsFilter, getVendorAccruals]
  );

  const fetchTransactions = useCallback(
    (offset, append) => {
      // Searching by transaction number looks across all dates and types,
      // so the due range and the type chips are left out of the request.
      // consideration dates are calendar days (yyyymmdd) with no time or zone,
      // so unlike the accrual timestamps they cannot shift across a UTC/IST boundary.
      const filterParams = searchTerm
        ? {}
        : {
            consideration_start_date: dueRange.from
              ? dueRange.from.replace(/-/g, "")
              : undefined,
            consideration_end_date: dueRange.to
              ? dueRange.to.replace(/-/g, "")
              : undefined,
            ...buildTransactionParams(transactionsFilter),
          };
      getVendorTransactions(
        {
          page_start_offset: offset,
          page_size: DEFAULT_PAGE_SIZE,
          entity_id: toEntityId,
          entity_type: TO_ENTITY_TYPE,
          transaction_number: searchTerm,
          ...filterParams,
        },
        { append }
      );
    },
    [toEntityId, dueRange, transactionsFilter, searchTerm, getVendorTransactions]
  );

  useEffect(() => {
    if (activeTab === PAYOUTS_TABS.PAYMENTS) {
      fetchAccruals(0, false);
    } else {
      fetchTransactions(0, false);
    }
  }, [activeTab, fetchAccruals, fetchTransactions]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (!nearBottom) return;

    if (activeTab === PAYOUTS_TABS.PAYMENTS) {
      if (accrualsStatus === STATUS_BEGIN || !accrualsHasMore) return;
      fetchAccruals(accruals.length, true);
    } else {
      if (transactionsStatus === STATUS_BEGIN || !transactionsHasMore) return;
      fetchTransactions(transactions.length, true);
    }
  }, [
    activeTab,
    accruals.length,
    accrualsStatus,
    accrualsHasMore,
    transactions.length,
    transactionsStatus,
    transactionsHasMore,
    fetchAccruals,
    fetchTransactions,
  ]);

  const isPayments = activeTab === PAYOUTS_TABS.PAYMENTS;
  const loading = isPayments
    ? accrualsStatus === STATUS_BEGIN
    : transactionsStatus === STATUS_BEGIN;
  const items = isPayments ? accruals : transactions;
  const statusFilters = isPayments
    ? PAYMENT_STATUS_FILTERS
    : TRANSACTION_STATUS_FILTERS;
  const activeFilter = isPayments ? paymentsFilter : transactionsFilter;
  const setFilter = isPayments ? setPaymentsFilter : setTransactionsFilter;

  const toggleFilter = (key) => {
    setFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const removeFilter = (key, e) => {
    e.stopPropagation();
    setFilter((prev) => prev.filter((k) => k !== key));
  };

  const countLabel = useMemo(() => {
    const n = items?.length || 0;
    return isPayments ? `${n} payments` : `${n} transactions`;
  }, [items, isPayments]);

  return (
    <React.Fragment>
      <Wrapper
        className="po-header"
        style={{
          background: "#F4F6FB",
          height: "auto",
          minHeight: 0,
          padding: "12px 16px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 16,
        }}
      >
        {searchOpen && !isPayments ? (
          <SearchBar onSubmit={handleSearchSubmit}>
            <SearchField>
              <SearchBackButton
                type="button"
                aria-label="Close search"
                onClick={closeSearch}
              >
                <ChevronLeftIcon />
              </SearchBackButton>
              <SearchInput
                type="search"
                enterKeyHint="search"
                placeholder="Search Transaction Number"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                ref={searchInputRef}
              />
            </SearchField>
          </SearchBar>
        ) : (
          <>
            <HeaderGroup>
              <HeaderBackWrap onClick={handleBackClick}>
                <BackButton />
              </HeaderBackWrap>
              <HeaderText>
                <HeaderTitle>Payments</HeaderTitle>
                {vendorName ? <HeaderSubtitle>{vendorName}</HeaderSubtitle> : null}
              </HeaderText>
            </HeaderGroup>
            {!isPayments ? (
              <HeaderIconButton
                type="button"
                aria-label="Search"
                onClick={() => setSearchOpen(true)}
              >
                <SearchIcon />
              </HeaderIconButton>
            ) : null}
          </>
        )}
      </Wrapper>
      <Screen>
        <TabBar>
          <TabItem
            active={isPayments}
            onClick={() => switchTab(PAYOUTS_TABS.PAYMENTS)}
          >
            Payments
          </TabItem>
          <TabItem
            active={!isPayments}
            onClick={() => switchTab(PAYOUTS_TABS.TRANSACTIONS)}
          >
            Transactions
          </TabItem>
        </TabBar>

        <FilterRow>
          <DateRangeChip
            from={dueRange.from}
            to={dueRange.to}
            onApply={setDueRange}
          />
          {statusFilters.map((f) => {
            const selected = activeFilter.includes(f.key);
            return (
              <FilterChip
                key={f.key}
                active={selected}
                onClick={() => !selected && toggleFilter(f.key)}
              >
                {f.label}
                {selected ? (
                  <span
                    role="button"
                    aria-label={`Remove ${f.label} filter`}
                    onClick={(e) => removeFilter(f.key, e)}
                    style={{ marginLeft: 2, cursor: "pointer", fontSize: 10 }}
                  >
                    ✕
                  </span>
                ) : null}
              </FilterChip>
            );
          })}
        </FilterRow>

        <ListHeader>
          <CountText>{countLabel}</CountText>
          <AdviceLink
            type="button"
            onClick={handleDownload}
            disabled={downloading}
          >
            <span>Payment advice</span>
            <DownloadIcon />
          </AdviceLink>
        </ListHeader>

        <ScrollList ref={scrollRef} onScroll={handleScroll}>
          {loading && !items?.length ? (
            <Loader />
          ) : items?.length ? (
            items.map((item, idx) =>
              isPayments ? (
                <PaymentRow
                  key={item.reference_number || idx}
                  item={item}
                  onClick={
                    item.reference_number
                      ? () => handlePaymentClick(item.reference_number)
                      : undefined
                  }
                />
              ) : (
                <TransactionRow
                  key={item.transaction_number || idx}
                  item={item}
                  onClick={() => setSelectedTransaction(item)}
                />
              )
            )
          ) : (
            <EmptyState>
              No {isPayments ? "payments" : "transactions"} to show
            </EmptyState>
          )}
          {loading && items?.length ? <Loader /> : null}
        </ScrollList>
      </Screen>
      {selectedTransaction ? (
        <TransactionDetailSheet
          item={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      ) : null}
      {downloadError ? (
        <ShowToast
          type="error"
          errorMsg={downloadError}
          onClose={() => setDownloadError("")}
        />
      ) : null}
    </React.Fragment>
  );
};

export default Payouts;
